import { Fragment, useMemo } from 'react';
import { Plus } from 'lucide-react';
import { Button } from '@marine/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@marine/ui/dropdown-menu';
import { useModules } from '@/contexts/ModuleContext';
import { useLayout } from '../components/context';
import { useQuickAddStore } from '@/state/quickAddStore';
import { MODULE_THEME, type QuickAddModule } from './moduleTheme';
import { QUICK_ADD_ACTIONS, type QuickAddAction } from './quickAddActions';

const GROUP_ORDER: QuickAddModule[] = ['core', 'inventory', 'crm', 'service', 'finance'];

interface QuickAddMenuProps {
  portalType?: 'platform' | 'tenant';
}

export function QuickAddMenu({ portalType = 'tenant' }: QuickAddMenuProps) {
  if (portalType !== 'tenant') return null;
  return <QuickAddMenuContent />;
}

function QuickAddMenuContent() {
  const { isMobile } = useLayout();
  const { hasModule } = useModules();
  const openDialog = useQuickAddStore((s) => s.open);

  const groups = useMemo(() => {
    return GROUP_ORDER.flatMap<{ module: QuickAddModule; actions: QuickAddAction[] }>((mod) => {
      if (!hasModule(mod)) return [];
      const actions = QUICK_ADD_ACTIONS.filter((a) => a.module === mod);
      return actions.length > 0 ? [{ module: mod, actions }] : [];
    });
  }, [hasModule]);

  if (groups.length === 0) return null;

  const handleSelect = (action: QuickAddAction) => {
    openDialog(action.id);
  };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="mono" mode={isMobile ? 'icon' : undefined} aria-label="Add">
          <Plus />
          {!isMobile && <span>Add</span>}
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" side="bottom" align="end" sideOffset={8}>
        {groups.map((group, idx) => {
          const theme = MODULE_THEME[group.module];
          return (
            <Fragment key={group.module}>
              {idx > 0 && <DropdownMenuSeparator />}
              <DropdownMenuLabel>{theme.label}</DropdownMenuLabel>
              <DropdownMenuGroup>
                {group.actions.map((action) => {
                  const Icon = action.icon;
                  return (
                    <DropdownMenuItem
                      key={action.id}
                      onSelect={() => handleSelect(action)}
                    >
                      <Icon className={theme.iconTextClass} />
                      <span>{action.label}</span>
                    </DropdownMenuItem>
                  );
                })}
              </DropdownMenuGroup>
            </Fragment>
          );
        })}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
